/**
 * Target auto-detection — pure heuristic that maps a raw target string
 * (URL, IP, CIDR, account ID, repo URL, …) to a CTB AssessmentType +
 * TargetKind. Drives the Target-Aware Scoping Wizard's "What do you want
 * CTB to test?" entry experience: paste anything, the system guesses the
 * type, the user confirms or overrides before saving.
 *
 * Heuristic ordering (most specific first):
 *
 *   1. Mobile build files (.apk / .ipa / App Store / Play Store URL)
 *   2. Code review (github.com / gitlab.com / bitbucket.org / azure repos)
 *   3. M365 tenants (*.onmicrosoft.com / *.sharepoint.com)
 *   4. Cloud account IDs (12-digit AWS, GUID Azure, alnum GCP)
 *   5. Internal vs external network — CIDR notation, RFC1918 → internal
 *   6. API — URL contains api., /api/, /v1/, /graphql, openapi, swagger
 *   7. Web App — default for anything else with http(s):// or a hostname
 *
 * Everything is a heuristic, never authoritative. The UI ALWAYS shows
 * "Detected as: X — change?" so the user has the final say.
 */

import type { AssessmentType, TargetKind, AssetCategory } from './questionnaires';

export type DetectionResult = {
  /** Best-guess assessment type. */
  assessmentType: AssessmentType;
  /** Best-guess target kind (used to seed TARGET_KIND_DEFAULT). */
  targetKind: TargetKind;
  /** How confident the heuristic is — drives whether the UI nudges the user
   *  to confirm. "high" matches the pattern strongly; "low" is a fallback. */
  confidence: 'high' | 'low';
  /** Short human-readable reason for the pick. Shown next to the detected
   *  label so the user understands WHY it picked Web vs API vs Network. */
  reason: string;
  /** Hostname-or-equivalent prefix used for auto-grouping under one
   *  business asset (e.g. "example.com" for two URLs that share a host). */
  groupingKey: string;
  /** Auto-generated business-asset name if the user doesn't override.
   *  Hidden from the UI in the target-first flow but persisted to the
   *  backend data model. */
  suggestedAssetName: string;
};

// ─── Regex bank ─────────────────────────────────────────────────────────────

const RX = {
  mobileApk: /\.apk(\?|$)/i,
  mobileIpa: /\.ipa(\?|$)/i,
  appStore: /\bapps\.apple\.com\b/i,
  playStore: /\bplay\.google\.com\b/i,
  // Bundle ID — reverse-DNS notation, FIRST segment must be a country-code
  // or generic TLD-style prefix (com, org, io, app, …) so that we don't
  // misclassify plain forward-DNS hostnames like api.example.com or
  // acme.onmicrosoft.com (which both have the same dot-separated shape).
  bundleId: /^(com|org|net|io|app|dev|co|nz|au|uk|us|de|jp)\.[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/i,

  github: /\bgithub\.com\b/i,
  gitlab: /\bgitlab\.com\b/i,
  bitbucket: /\bbitbucket\.org\b/i,
  azureRepos: /\bdev\.azure\.com\b|\bvisualstudio\.com\b/i,

  m365Tenant: /\.onmicrosoft\.com\b|\.sharepoint\.com\b/i,

  awsAccount: /^\s*\d{12}\s*$/,
  azureSub: /^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/i,
  gcpProject: /^\s*[a-z][a-z0-9-]{4,28}[a-z0-9]\s*$/i,
  awsLabel: /\bAWS\s+(account|acct)\b/i,
  azureLabel: /\bAzure\s+subscription\b/i,
  gcpLabel: /\bGCP\s+project\b/i,

  cidr: /\b((?:\d{1,3}\.){3}\d{1,3})\/(\d{1,2})\b/,
  ipv4: /\b((?:\d{1,3}\.){3}\d{1,3})\b/,

  // RFC1918 + link-local + carrier-grade NAT
  rfc1918: /\b(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|169\.254\.|100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.)/,

  apiKeyword: /\bapi\.|\/api\/|\/v[0-9]+(\/|$)|\/graphql|swagger|openapi/i,
  // Generic URL — anything starting with http(s):// or containing a dot + slash.
  url: /^https?:\/\//i,
  hostname: /^([a-z0-9-]+\.)+[a-z]{2,}/i,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Extract a usable hostname/equivalent for grouping. Strips scheme + path. */
function extractHost(s: string): string | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    return u.hostname || null;
  } catch {
    return null;
  }
}

/** Best-effort "registered" domain — strip subdomain so app.example.com and
 *  api.example.com both group under "example.com". Naive: drops first label
 *  if there are at least 3. Good enough for *.example.com / *.acme.co.nz. */
function registeredDomain(host: string): string {
  const parts = host.split('.');
  if (parts.length <= 2) return host;
  // Heuristic ccTLD pairs (.co.nz, .com.au, etc.) — keep last 3 labels.
  const tail2 = parts.slice(-2).join('.');
  const ccPairs = new Set([
    'co.nz', 'co.uk', 'co.au', 'co.za', 'co.in', 'co.jp',
    'com.au', 'com.br', 'com.cn', 'com.tw', 'com.sg', 'com.hk',
    'net.au', 'org.au', 'gov.au', 'edu.au',
  ]);
  if (ccPairs.has(tail2)) return parts.slice(-3).join('.');
  return parts.slice(-2).join('.');
}

// ─── Main detection ─────────────────────────────────────────────────────────

export function detectTargetType(raw: string): DetectionResult | null {
  const s = (raw ?? '').trim();
  if (!s) return null;

  // 1. Mobile build refs
  if (RX.mobileApk.test(s)) {
    return base('MOBILE_APP', 'APK_FILE', 'high', 'Looks like an Android build (.apk)', s, fileNameOf(s));
  }
  if (RX.mobileIpa.test(s)) {
    return base('MOBILE_APP', 'IPA_FILE', 'high', 'Looks like an iOS build (.ipa)', s, fileNameOf(s));
  }
  if (RX.appStore.test(s) || RX.playStore.test(s)) {
    return base('MOBILE_APP', 'APP_STORE_URL', 'high', 'Looks like an app-store listing', extractHost(s) ?? s, niceUrlAsset(s));
  }
  if (RX.bundleId.test(s) && !s.includes('://')) {
    return base('MOBILE_APP', 'OTHER', 'low', 'Looks like an app bundle identifier (com.example.app)', s, s);
  }

  // 2. Code repositories
  if (RX.github.test(s) || RX.gitlab.test(s) || RX.bitbucket.test(s) || RX.azureRepos.test(s)) {
    const host = extractHost(s);
    // Asset name = "org/repo" so two branches/PRs in same repo group under one asset.
    let repoPath = '';
    try {
      const u = new URL(s.includes('://') ? s : `https://${s}`);
      const parts = u.pathname.split('/').filter(Boolean);
      repoPath = parts.slice(0, 2).join('/');
    } catch { /* ignore */ }
    return base('CODE_REVIEW', 'REPOSITORY_URL', 'high', 'Looks like a source-code repository', host ?? s, repoPath || (host ?? s));
  }

  // 3. M365 / SharePoint tenants
  if (RX.m365Tenant.test(s)) {
    const host = extractHost(s) ?? s;
    return base('M365_REVIEW', 'TENANT_ID', 'high', 'Looks like an M365 / SharePoint tenant', host, host);
  }

  // 4. Cloud account IDs
  if (RX.awsAccount.test(s) || RX.awsLabel.test(s)) {
    return base('CLOUD_CONFIG', 'AWS_ACCOUNT_ID', 'high', '12-digit ID looks like an AWS account', `aws:${s.replace(/\D/g, '')}`, `AWS ${s.replace(/\D/g, '').slice(0, 12) || 'account'}`);
  }
  if (RX.azureSub.test(s) || RX.azureLabel.test(s)) {
    return base('CLOUD_CONFIG', 'AZURE_SUBSCRIPTION_ID', 'high', 'GUID looks like an Azure subscription', `azure:${s}`, `Azure subscription`);
  }
  if (RX.gcpLabel.test(s)) {
    return base('CLOUD_CONFIG', 'GCP_PROJECT_ID', 'high', 'Looks like a GCP project', `gcp:${s}`, `GCP project`);
  }

  // 5. Networks (CIDR > IP > hostname)
  const cidr = RX.cidr.exec(s);
  if (cidr) {
    const isInternal = RX.rfc1918.test(cidr[1]);
    return base(
      isInternal ? 'INTERNAL_NETWORK' : 'EXTERNAL_NETWORK',
      'CIDR',
      'high',
      isInternal ? 'Private (RFC1918) CIDR — looks like an internal network' : 'CIDR range — looks like an external network',
      cidr[0],
      cidr[0],
    );
  }
  const ipMatch = RX.ipv4.exec(s);
  if (ipMatch && !s.includes('://')) {
    const isInternal = RX.rfc1918.test(ipMatch[1]);
    return base(
      isInternal ? 'INTERNAL_NETWORK' : 'EXTERNAL_NETWORK',
      'IP',
      'high',
      isInternal ? 'Private IPv4 — looks like an internal target' : 'Public IPv4 — looks like an external target',
      ipMatch[1],
      ipMatch[1],
    );
  }

  // 6. API vs Web app
  if (RX.apiKeyword.test(s)) {
    return base('API', 'API_BASE_URL', 'high', 'URL pattern looks like an API (api., /api/, /v1/, GraphQL, …)', extractHost(s) ?? s, niceUrlAsset(s));
  }

  // 7. Generic web fallback
  if (RX.url.test(s) || RX.hostname.test(s)) {
    return base('WEB_APP', 'BASE_URL', 'high', 'URL — defaulting to Web Application', extractHost(s) ?? s, niceUrlAsset(s));
  }

  // Last resort — return a low-confidence Web pick so the customer sees the
  // "change?" affordance and can correct.
  return base('WEB_APP', 'OTHER', 'low', 'Unrecognised input — defaulting to Web (change if needed)', s, s);
}

// ─── Build helpers ──────────────────────────────────────────────────────────

function base(
  assessmentType: AssessmentType,
  targetKind: TargetKind,
  confidence: 'high' | 'low',
  reason: string,
  groupingKey: string,
  suggestedAssetName: string,
): DetectionResult {
  // Normalise grouping key — for URLs, use the registered domain so
  // app.example.com and api.example.com share the same business asset.
  const normalisedGroupingKey = (() => {
    const host = extractHost(groupingKey);
    return host ? registeredDomain(host) : groupingKey;
  })();
  return {
    assessmentType,
    targetKind,
    confidence,
    reason,
    groupingKey: normalisedGroupingKey,
    suggestedAssetName,
  };
}

function fileNameOf(s: string): string {
  const slash = s.lastIndexOf('/');
  return slash >= 0 ? s.slice(slash + 1) : s;
}

function niceUrlAsset(s: string): string {
  const host = extractHost(s);
  return host ? registeredDomain(host) : s;
}

// ─── Asset-category mapping ─────────────────────────────────────────────────
// Used by the target-first builder to silently set the parent business
// asset's category when auto-creating it. The user never sees this field.


export const ASSESSMENT_TO_CATEGORY: Record<AssessmentType, AssetCategory> = {
  WEB_APP: 'DIGITAL_APPLICATIONS',
  API: 'DIGITAL_APPLICATIONS',
  MOBILE_APP: 'DIGITAL_APPLICATIONS',
  AI_LLM: 'DIGITAL_APPLICATIONS',
  EXTERNAL_NETWORK: 'INFRASTRUCTURE_NETWORKS',
  INTERNAL_NETWORK: 'INFRASTRUCTURE_NETWORKS',
  VPN_FIREWALL_REVIEW: 'INFRASTRUCTURE_NETWORKS',
  CLOUD_CONFIG: 'CLOUD_SAAS_IDENTITY',
  CLOUD_PENTEST: 'CLOUD_SAAS_IDENTITY',
  M365_REVIEW: 'CLOUD_SAAS_IDENTITY',
  CODE_REVIEW: 'CODE_SUPPLY_CHAIN',
};
